import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useEffectiveClientId } from '@/hooks/useAuth'
import {
  compass, spread, hasXY, isLocal, NEAR_COUNTRIES,
  type CataloguePlace, type NearPlace,
} from '@/lib/placeCatalogue'

/**
 * The place catalogue (migration 20240047; Brian's after/places.js v2.7): search, the places within a radius, the
 * radius to open on, and the account's own places read against it. Shared by onboarding and Settings › Places.
 */

const COLS = 'id, name, region, kind, st, country, country_code, continent, scope, tier, lat, lng, news_only, watch, languages, region_word, nreg'
const WEEK = 7 * 24 * 60 * 60_000

/** Ranked matches for what's typed (search_places). prefer: a country name whose places come first. */
export function usePlaceSearch(q: string, opts: { limit?: number; prefer?: string | null } = {}) {
  const term = q.trim()
  return useQuery({
    queryKey: ['place-search', term.toLowerCase(), opts.limit ?? 8, opts.prefer ?? null],
    enabled: term.length >= 2,
    staleTime: WEEK,
    placeholderData: (prev) => prev,
    queryFn: async () => {
      const { data, error } = await supabase.rpc('search_places', { q: term, lim: opts.limit ?? 8, prefer: opts.prefer ?? null })
      if (error) throw error
      return (data ?? []) as CataloguePlace[]
    },
  })
}

export function usePlaceById(id: number | null | undefined) {
  return useQuery({
    queryKey: ['place', id],
    enabled: id != null,
    staleTime: WEEK,
    queryFn: async () => {
      const { data, error } = await supabase.from('place_catalogue').select(COLS).eq('id', id!).maybeSingle()
      if (error) throw error
      return (data as CataloguePlace | null) ?? null
    },
  })
}

/** An account's own place string ("Bend" with the account's state "OR") read against the catalogue (find()). */
export function usePlaceLookup(name: string | null | undefined, region: string | null | undefined, enabled = true) {
  return useQuery({
    queryKey: ['place-lookup', name, region],
    enabled: enabled && !!name,
    staleTime: WEEK,
    queryFn: async () => {
      const { data, error } = await supabase.rpc('place_lookup', { p_name: name!, p_region: region ?? null })
      if (error) throw error
      return ((data ?? []) as CataloguePlace[])[0] ?? null
    },
  })
}

/** The radius to open on: the smallest of 10 · 25 · 50 · 100 · 200 miles that holds three places. */
export function useDefaultRadius(home: CataloguePlace | null | undefined, onlyHome: boolean) {
  return useQuery({
    queryKey: ['place-radius', home?.id, onlyHome],
    enabled: isLocal(home) && hasXY(home),
    staleTime: WEEK,
    queryFn: async () => {
      const { data, error } = await supabase.rpc('place_default_radius', {
        p_lat: home!.lat!, p_lng: home!.lng!, p_country: onlyHome ? home!.country : null, p_exclude: home!.id, p_home_country: home!.country,
      })
      if (error) throw error
      return data as number
    },
  })
}

export interface Neighbours {
  /** One per compass direction first, nearest first out. */
  cities: NearPlace[]
  /** How many sit within the radius. */
  total: number
  /** Something within the radius is across a border: the border switch shows. */
  abroad: boolean
  /** A region's or a country's neighbours (no radius). */
  siblings?: boolean
}

async function near(lat: number, lng: number, radius: number, kinds: string[], country: string | null, exclude: number, home: string | null, limit = 300) {
  const { data, error } = await supabase.rpc('places_near', {
    p_lat: lat, p_lng: lng, p_radius: radius, p_kinds: kinds, p_country: country, p_exclude: exclude, p_home_country: home, p_limit: limit,
  })
  if (error) throw error
  return ((data ?? []) as Omit<NearPlace, 'dir'>[]).map((r) => ({ ...r, miles: Math.round(r.miles), dir: compass(r.deg) })) as NearPlace[]
}

/**
 * The places next door (places.js near). A town or county: the places within the radius, larger first, spread one per
 * compass direction, inside the home country unless the borders are open. A region abroad: its neighbouring regions.
 * A country abroad: its nearest countries.
 */
export function usePlaceNear(home: CataloguePlace | null | undefined, radius: number | null | undefined, onlyHome: boolean, n = 8) {
  return useQuery({
    queryKey: ['place-near', home?.id, radius ?? 0, onlyHome, n],
    enabled: !!home && hasXY(home) && (!isLocal(home) || !!radius),
    staleTime: WEEK,
    placeholderData: (prev) => prev,
    queryFn: async (): Promise<Neighbours> => {
      const p = home!
      if (isLocal(p)) {
        const rows = await near(p.lat!, p.lng!, radius!, ['city'], onlyHome ? p.country : null, p.id, p.country)
        return { cities: spread(rows, n, radius!), total: rows[0]?.total ?? 0, abroad: rows[0]?.abroad ?? false }
      }
      if (p.kind === 'region') {
        const rows = await near(p.lat!, p.lng!, 5000, ['region'], p.country, p.id, p.country, 200)
        const byMiles = [...rows].sort((a, b) => a.miles - b.miles)
        return { cities: spread(byMiles, n, Math.max(1, ...byMiles.map((r) => r.miles))), total: rows.length, abroad: false, siblings: true }
      }
      if (p.kind === 'country' && p.country !== 'United States' && p.country !== 'Canada') {
        const rows = await near(p.lat!, p.lng!, 20000, ['country'], null, p.id, p.country, 60)
        const byMiles = [...rows].sort((a, b) => a.miles - b.miles)
        return { cities: spread(byMiles, n, Math.max(1, ...byMiles.map((r) => r.miles))), total: rows.length, abroad: false, siblings: true }
      }
      return { cities: [], total: 0, abroad: false }
    },
  })
}

/** A city's county and its state or province (the wider rows under the dial). */
export function useHomeParents(home: CataloguePlace | null | undefined) {
  return useQuery({
    queryKey: ['place-parents', home?.id],
    enabled: !!home && !!home.st,
    staleTime: WEEK,
    queryFn: async () => {
      const p = home!
      const countyName = p.kind === 'city' && p.country === 'United States' && !/^Independent city/.test(p.region)
        ? p.region.replace(/, [A-Z]{2}$/, '') : null
      const [county, state] = await Promise.all([
        countyName
          ? supabase.from('place_catalogue').select(COLS).eq('kind', 'county').eq('st', p.st!).eq('name', countyName).maybeSingle()
          : Promise.resolve({ data: null, error: null }),
        p.kind === 'state' || p.kind === 'province'
          ? Promise.resolve({ data: null, error: null })
          : supabase.from('place_catalogue').select(COLS).in('kind', ['state', 'province']).eq('st', p.st!).maybeSingle(),
      ])
      if (county.error) throw county.error
      if (state.error) throw state.error
      return { county: (county.data as CataloguePlace | null) ?? null, state: (state.data as CataloguePlace | null) ?? null }
    },
  })
}

/** The wide scopes: the account's country (its National), the continent, the countries it looks at first, the World. */
export function useWideScopes(country: string | null | undefined) {
  const c = country || 'United States'
  return useQuery({
    queryKey: ['place-wide', c],
    staleTime: WEEK,
    queryFn: async () => {
      const names = [c, ...(NEAR_COUNTRIES[c] ?? [])].map((n) => `"${n.replace(/"/g, '')}"`).join(',')
      const { data, error } = await supabase.from('place_catalogue').select(COLS)
        .or(`kind.in.(continent,world),and(kind.eq.country,name.in.(${names}))`)
      if (error) throw error
      const rows = (data ?? []) as CataloguePlace[]
      const national = rows.find((r) => r.kind === 'country' && r.name === c) ?? null
      const continent = rows.find((r) => r.kind === 'continent' && r.name === national?.continent) ?? null
      const others = (NEAR_COUNTRIES[c] ?? []).map((n) => rows.find((r) => r.kind === 'country' && r.name === n)).filter(Boolean) as CataloguePlace[]
      return { national, continent, others, world: rows.find((r) => r.kind === 'world') ?? null, continents: rows.filter((r) => r.kind === 'continent') }
    },
  })
}

/** A country's regions (a country home abroad: "Add a county of Ireland"). */
export function useCountryRegions(country: string | null | undefined, enabled = true) {
  return useQuery({
    queryKey: ['place-regions', country],
    enabled: enabled && !!country,
    staleTime: WEEK,
    queryFn: async () => {
      const { data, error } = await supabase.from('place_catalogue').select(COLS).eq('kind', 'region').eq('country', country!).order('name').limit(40)
      if (error) throw error
      return (data ?? []) as CataloguePlace[]
    },
  })
}

/** Records which catalogue place a region is (after add_region, swap_place or change_home_place). */
export function usePinPlace() {
  const clientId = useEffectiveClientId()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (args: { regionId: string; placeId: number }) => {
      const { error } = await supabase.rpc('pin_region_place', { p_region: args.regionId, p_place: args.placeId })
      if (error) throw error
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['regions', clientId] }),
  })
}

/** The radius and border choice for "next door" (clients.near_radius, near_borders), saved with the account. */
export function useNearPrefs() {
  const clientId = useEffectiveClientId()
  return useQuery({
    queryKey: ['near-prefs', clientId],
    enabled: !!clientId,
    queryFn: async () => {
      const { data, error } = await supabase.from('clients').select('near_radius, near_borders, language, locale, onboarding').eq('id', clientId!).maybeSingle()
      if (error) return { near_radius: null, near_borders: 'home', language: 'en', locale: 'en-US', onboarding: {} } as NearPrefs
      return data as NearPrefs
    },
  })
}
export interface NearPrefs {
  near_radius: number | null
  near_borders: 'home' | 'open'
  language: 'en' | 'es' | 'bilingual'
  locale: string
  onboarding: Record<string, unknown>
}

export function useSaveNearPrefs() {
  const clientId = useEffectiveClientId()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (patch: Partial<Pick<NearPrefs, 'near_radius' | 'near_borders'>>) => {
      const { error } = await supabase.from('clients').update(patch).eq('id', clientId!)
      if (error) throw error
    },
    onMutate: (patch) => {
      qc.setQueryData<NearPrefs>(['near-prefs', clientId], (old) => (old ? { ...old, ...patch } : old))
    },
    onSettled: () => qc.invalidateQueries({ queryKey: ['near-prefs', clientId] }),
  })
}

/** A state or province home: its larger places first (settings-places v3). */
export function useStateCities(st: string | null | undefined, enabled = true) {
  return useQuery({
    queryKey: ['place-state-cities', st],
    enabled: enabled && !!st,
    staleTime: WEEK,
    queryFn: async () => {
      const { data, error } = await supabase.from('place_catalogue').select(COLS).eq('kind', 'city').eq('st', st!)
        .gt('tier', 0).order('tier').order('name').limit(12)
      if (error) throw error
      return (data ?? []) as CataloguePlace[]
    },
  })
}

/**
 * The account's home place in the catalogue: the place it was pinned to, else its name read against the catalogue
 * with the account's state (an account from before the catalogue: "Bend" and "OR").
 */
export function useHomePlace(home: { place_id?: number | null; name: string } | null | undefined, state: string | null | undefined) {
  const byId = usePlaceById(home?.place_id ?? null)
  const byName = usePlaceLookup(home?.name, state, !!home && home.place_id == null)
  return home?.place_id != null ? byId : byName
}
